import type { Entity } from '@backstage/catalog-model';
import type { RouteRef } from '@backstage/frontend-plugin-api';
import type { defaultEntityContentGroupDefinitions } from '@backstage/plugin-catalog-react/alpha';
import type { LooseString } from '@ipen-portal/plugin-common';
import type {
  ComponentProps,
  ComponentType,
  JSX,
  PropsWithChildren,
  ReactElement,
} from 'react';

import {
  coreExtensionData,
  createExtensionBlueprint,
  createExtensionDataRef,
  ExtensionBoundary,
} from '@backstage/frontend-plugin-api';
import {
  EntityContentBlueprint,
  EntityHeaderBlueprint,
} from '@backstage/plugin-catalog-react/alpha';

import type {
  IpenEntityListFilterProvider,
  IpenEntityListToolbarElements,
} from '../components';

export const layoutWrapperComponent = createExtensionDataRef<
  (props: PropsWithChildren<{ entity: Entity }>) => ReactElement
>().with({
  id: 'ipen.entity-content.layout-wrapper',
});

export const toolbarElements = createExtensionDataRef<
  (props: { entity: Entity }) => JSX.Element
>().with({
  id: 'ipen.entity-content.toolbar-elements',
});

export type EntityListFilterProps = Omit<
  ComponentProps<typeof IpenEntityListFilterProvider>,
  'children'
>;

export type EntityListToolbarProps = ComponentProps<
  typeof IpenEntityListToolbarElements
>;

export type FactoryParams = {
  filter?: (entity: Entity) => boolean;
  group?: LooseString<keyof typeof defaultEntityContentGroupDefinitions>;
  icon?: ReactElement | string;
  loader: () => Promise<JSX.Element>;
  path: string;
  routeRef?: RouteRef;
  title: string;
} & WrapperConfig
& ToolbarConfig;

type ToolbarConfig =
  | {
    toolbarElements: () => Promise<ComponentType<{ entity: Entity }>>;
  }
  | {
    toolbarProps?: EntityListToolbarProps;
  };

type WrapperConfig =
  | {
    wrapperComponent: () => Promise<
      ComponentType<PropsWithChildren<{ entity: Entity }>>
    >;
  }
  | {
    wrapperProps?: EntityListFilterProps;
  };

export const EntityContentPageBlueprint = createExtensionBlueprint({
  attachTo: { id: 'page:catalog/entity', input: 'contents' },
  dataRefs: {
    filterFunction: EntityContentBlueprint.dataRefs.filterFunction,
    group: EntityContentBlueprint.dataRefs.group,
    icon: EntityContentBlueprint.dataRefs.icon,
    title: EntityContentBlueprint.dataRefs.title,
  },
  * factory(factoryProps: FactoryParams, { node }) {
    const { filter, group, icon, loader, path, title } = factoryProps;

    yield coreExtensionData.routePath(path);
    yield EntityContentBlueprint.dataRefs.title(title);

    if (filter) {
      yield EntityHeaderBlueprint.dataRefs.filterFunction(filter);
    }

    if (group) {
      yield EntityContentBlueprint.dataRefs.group(group);
    }

    if (icon) {
      yield EntityContentBlueprint.dataRefs.icon(icon);
    }

    yield coreExtensionData.reactElement(ExtensionBoundary.lazy(node, loader));
  },
  kind: 'entity-content-page',

  output: [
    coreExtensionData.reactElement,
    coreExtensionData.routePath,
    EntityContentBlueprint.dataRefs.title,
    coreExtensionData.routeRef.optional(),
    EntityContentBlueprint.dataRefs.filterFunction.optional(),
    EntityContentBlueprint.dataRefs.group.optional(),
    EntityContentBlueprint.dataRefs.icon.optional(),
  ],
});

export const EntityContentLayoutWrapperBlueprint = createExtensionBlueprint({
  attachTo: { id: 'entity-layout:catalog/ipen-layout', input: 'layoutWrapper' },
  dataRefs: {
    filterFunction: EntityContentBlueprint.dataRefs.filterFunction,
    layoutWrapperComponent,
    toolbarElements,
  },
  * factory(factoryProps: FactoryParams, { node }) {
    const { filter, path } = factoryProps;

    yield coreExtensionData.routePath(path);

    if (filter) {
      yield EntityContentBlueprint.dataRefs.filterFunction(filter);
    }

    if ('wrapperComponent' in factoryProps) {
      yield layoutWrapperComponent(
        ExtensionBoundary.lazyComponent(node, async () => {
          const C = await factoryProps.wrapperComponent();
          return ({ children, entity }) => <C entity={entity}>{children}</C>;
        }),
      );
    } else if ('wrapperProps' in factoryProps) {
      const wrapperProps = factoryProps.wrapperProps;
      if (wrapperProps) {
        yield layoutWrapperComponent(
          ExtensionBoundary.lazyComponent(node, async () =>
            import('../components').then(m => ({ children }) => (
              <m.IpenEntityListFilterProvider {...wrapperProps}>
                {children}
              </m.IpenEntityListFilterProvider>
            )),
          ),
        );
      }
    }

    yield 'toolbarElements' in factoryProps
      ? toolbarElements(
          ExtensionBoundary.lazyComponent(node, async () => {
            const Component = await factoryProps.toolbarElements();
            return ({ entity }) => <Component entity={entity} />;
          }),
        )
      : toolbarElements(
          ExtensionBoundary.lazyComponent(node, async () => {
            const toolbarProps =
              'toolbarProps' in factoryProps
                ? factoryProps.toolbarProps
                : undefined;
            return import('../components').then(m => () => (
              <m.IpenEntityListToolbarElements {...toolbarProps} />
            ));
          }),
        );
  },
  kind: 'entity-content-layout-wrapper',

  output: [
    layoutWrapperComponent.optional(),
    toolbarElements,
    coreExtensionData.routePath,
    EntityContentBlueprint.dataRefs.filterFunction.optional(),
  ],
});

export const makeEntityContentWithToolbar = (params: {
  name?: string;
  params: FactoryParams;
}) => [
  EntityContentPageBlueprint.make({
    name: params.name,
    params: params.params,
  }),
  EntityContentLayoutWrapperBlueprint.make({
    name: params.name ? `${params.name}LayoutWrapper` : undefined,
    params: params.params,
  }),
];

export const EntityContentWithToolbarBlueprint = {
  dataRefs: EntityContentLayoutWrapperBlueprint.dataRefs,
};
